import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      cacheTime: 60000,
      staleTime: 60000,
    },
    mutations: {
      retry: false,
    },
  },
});

export enum QueryKeys {
  ACTIVE_PASSES = 'ACTIVE_PASSES',
  ACTIVITY_FEED_SITE_OPTIONS = 'ACTIVITY_FEED_SITE_OPTIONS',
  ADMIN_SITES = 'ADMIN_SITES',
  AI_PASSES_SUMMARY = 'AI_PASSES_SUMMARY',
  ASSET_HISTORY = 'ASSET_HISTORY',
  ASSET_TYPES = 'ASSET_TYPES',
  ASSETS = 'ASSETS',
  AUTH_STRATEGIES = 'AUTH_STRATEGIES',
  BILLING_CONTACT = 'BILLING_CONTACT',
  BILLING_MODE = 'BILLING_MODE',
  BILLING_SITES = 'BILLING_SITES',
  BILLING_USAGE = 'BILLING_USAGE',
  CMS_PLAN_DATA = 'CMS_PLAN_DATA',
  COMPANY_LOGO = 'COMPANY_LOGO',
  CUSTOMER = 'CUSTOMER',
  DELEGATES = 'DELEGATES',
  FORM_PREFILLED = 'FORM_PREFILLED',
  ICS_INVITE = 'ICS_INVITE',
  INACTIVE_PASSES = 'INACTIVE_PASSES',
  INTEGRATIONS_BY_TYPE = 'INTEGRATIONS_BY_TYPE',
  INVITATION_DETAILS = 'INVITATION_DETAILS',
  INVITATIONS = 'INVITATIONS',
  LOGIN_STRATEGIES = 'LOGIN_STRATEGIES',
  LINKED_SITES = 'LINKED_SITES',
  MY_APPOINTEES = 'MY_APPOINTEES',
  MY_HOST_PROFILES = 'MY_HOST_PROFILES',
  MY_PROFILE = 'MY_PROFILE',
  MY_PROFILE_IMAGE = 'MY_PROFILE_IMAGE',
  MY_SITES_WITH_PERMISSIONS = 'MY_SITES_WITH_PERMISSIONS',
  PARENT_SITE_OPTIONS = 'PARENT_SITE_OPTIONS',
  PARENT_VISITOR_TYPES = 'PARENT_VISITOR_TYPES',
  PARENT_VISITOR_TYPES_OPTIONS = 'PARENT_VISITOR_TYPES_OPTIONS',
  PASS_INFO = 'PASS_INFO',
  PRICES = 'PRICES',
  SITE_HISTORY = 'SITE_HISTORY',
  SITE_HOSTS = 'SITE_HOSTS',
  SITE_LOCATION = 'SITE_LOCATION',
  SITE_REQUIREMENTS = 'SITE_REQUIREMENTS',
  SITE_STATISTICS = 'SITE_STATISTICS',
  SUBSCRIPTIONS = 'SUBSCRIPTIONS',
  TEAM_FEATURES = 'TEAM_FEATURES',
  TEAM_METADATA = 'TEAM_METADATA',
  TEAMS_LIST = 'TEAMS_LIST',
  TRIAL_DATA = 'TRIAL_DATA',
  TUNNELS = 'TUNNELS',
  UPCOMING_PASSES = 'UPCOMING_PASSES',
  WORKFLOW_INFO = 'WORKFLOW_INFO',
  WORKFLOWS_TRIAL_DATA = 'WORKFLOWS_TRIAL_DATA',
  ZONE_HOSTGROUPS = 'ZONE_HOSTGROUPS',
  ZONE_OPTIONS = 'ZONE_OPTIONS',
  ZONES = 'ZONES',
  ZONES_HOST_PROFILE = 'ZONES_HOST_PROFILE',
}

export enum MutationKeys {
  ADD_APPOINTEE = 'ADD_APPOINTEE',
  CANCEL_LICENCE_ADDON = 'CANCEL_LICENCE_ADDON',
  CANCEL_WORKFLOW_TRIAL = 'CANCEL_WORKFLOW_TRIAL',
  CREATE_MANUAL_LICENCE = 'CREATE_MANUAL_LICENCE',
  CREATE_MANUAL_SUBSCRIPTION = 'CREATE_MANUAL_SUBSCRIPTION',
  COMPANIES_VERIFIED_OPTIONS = 'COMPANIES_VERIFIED_OPTIONS',
  CREATE_INVITATION = 'CREATE_INVITATION',
  CREATE_LICENCE_ADDON = 'CREATE_LICENCE_ADDON',
  CREATE_SUBSCRIPTION = 'CREATE_SUBSCRIPTION',
  DELEGATE_HOST_PROFILE = 'DELEGATE_HOST_PROFILE',
  DELEGATION_OPTIONS = 'DELEGATION_OPTIONS',
  DELETE_PROFILE = 'DELETE_PROFILE',
  DELETE_USER_DOCUMENT = 'DELETE_USER_DOCUMENT',
  ENABLE_2FA = 'ENABLE_2FA',
  EXTEND_WORKFLOW_TRIAL = 'EXTEND_WORKFLOW_TRIAL',
  IDENTITY_CHECK = 'IDENTITY_CHECK',
  MOBILE_VERIFICATION_CODE = 'MOBILE_VERIFICATION_CODE',
  MY_PROFILE_UPDATE = 'MY_PROFILE_UPDATE',
  PROFILE_UPGRADE = 'PROFILE_UPGRADE',
  REMOVE_2FA_STRATEGY = 'REMOVE_2FA_STRATEGY',
  REMOVE_APPOINTEE = 'REMOVE_APPOINTEE',
  RESEND_APPOINTEE_INVITE = 'RESEND_APPOINTEE_INVITE',
  SAVE_CMS_PLAN_DATA = 'SAVE_CMS_PLAN_DATA',
  SAVE_WORKFLOW_TRIAL_SETTINGS = 'SAVE_WORKFLOW_TRIAL_SETTINGS',
  UPDATE_CARDS = 'UPDATE_CARDS',
  UPDATE_HOST_PROFILE = 'UPDATE_HOST_PROFILE',
  UPDATE_INVITATION = 'UPDATE_INVITATION',
  UPDATE_MANUAL_SUBSCRIPTION = 'UPDATE_MANUAL_SUBSCRIPTION',
  UPDATE_SUBSCRIPTION = 'UPDATE_SUBSCRIPTION',
  UPLOAD_PROFILE_DOCUMENT = 'UPLOAD_PROFILE_DOCUMENT',
  VERIFY_MOBILE = 'VERIFY_MOBILE',
}

/**
 * @description Invalidates all queries that fuzzy match the keys.
 */
export function invalidateQueryCaches(keys: Array<QueryKeys>) {
  queryClient
    .getQueryCache()
    .getAll()
    .forEach((cache) => {
      if (keys.find((key) => cache.queryHash.includes(key))) {
        // Fire and forget
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryClient.invalidateQueries(cache.queryKey);
      }
    });
}
